/*
 * decaffeinate suggestions:
 * DS102: Remove unnecessary code created because of implicit returns
 * DS206: Consider reworking classes to avoid initClass
 * DS207: Consider shorter variations of null checks
 * Full docs: https://github.com/decaffeinate/decaffeinate/blob/master/docs/suggestions.md
 */
import {EventsHash} from 'backbone';
import $ from 'jquery';
import _ from 'underscore';

import * as analytics from 'analytics/Analytics';
import BenchlingConfig from 'BenchlingConfig';
import {Sequence as BioJSSequence} from 'biojs/Sequence';
import PolymerBufferTabView from 'components/PolymerBufferTabView';
import * as selectioncontextmenu from 'components/SelectionContextMenu';
import {MenuChoicesOptions} from 'components/SelectionContextMenuHelpers';
import {Sequence} from 'components/Sequence';
import {SequenceType} from 'constants/SequenceConstants';
import checkSoftInvariant from 'lib/checkSoftInvariant';
import AaSequence from 'molbio/aa-sequences/AaSequence';
import * as ProteinContextMenus from 'molbio/aa-sequences/AaSequenceContextMenus';
import {BUBBLE_WIDTH, INPUT_BUBBLE_OPERATION} from 'molbio/maps/InputBubble';
import InputBubbleView, {InputBubblePositionDetails} from 'molbio/maps/linear/InputBubbleView';
import {Selection} from 'molbio/maps/selectables/Selection';
import {ShowInputBubbleOptions} from 'molbio/maps/SequenceMapUtil';
import PolymerMapTabState from 'molbio/tabstates/PolymerMapTabState';
import notifications from 'notifications';
import * as keyboard from 'util/Keyboard';

/**
 * This abstract class provides keyboard support to a TabView that allows manipulating a selection
 * (arrow keys to navigate, Ctrl-A, Ctrl-C, Ctrl-V), and inserting residues via the InputBubbleView.
 *
 * A subclass of PolymerMapTabView should define `mapstate` or override the methods
 * (getSelection, getResidueSlice, undo) that use it.
 */
abstract class PolymerMapTabView<
  M extends PolymerMapTabState<Sequence | AaSequence>,
> extends PolymerBufferTabView<M> {
  inputSource!: string;
  inputBubbleView: InputBubbleView | null = null;
  // By default, the input bubble is positioned below the the slice view, and will flip to being
  // positioned above the slice if the bubble is close to going off the screen.
  allowBubblePositionedAboveSlice: boolean = true;

  // NOTE: the inheritance is messy here - some subclasses define a mapstate while others just override the
  // methods that require a mapstate.
  // oxlint-disable-next-line @typescript-eslint/no-explicit-any
  abstract mapstate: any;

  static override initClass(): void {
    this.prototype.inputSource = 'maptab-input';
  }

  override events(): EventsHash {
    return {
      keydown: 'handleKeydown',
      contextmenu: (e) => {
        this.handleContextMenu(e);
      },
    };
  }

  focusElement(): JQuery {
    return this.$el;
  }

  getSelection(): Selection {
    return (this.mapstate.polymer() as AaSequence | Sequence).selection();
  }

  getResidueSlice(start: number, end: number): string {
    return (this.mapstate.polymer() as AaSequence | Sequence).residueSlice(start, end);
  }

  undo(): void {
    if ((this.mapstate.polymer() as Sequence).allowResidueEdits({ignoreVersion: true})) {
      (this.mapstate.polymer() as Sequence).undo();
    }
  }

  /**
   Gets the Benchling Clipboard hash for the current selection and stores the
   selection contents and metadata in the Benchling Clipboard.
   */
  abstract storeRegionInBenchlingClipboard(e: JQueryEventObject): string;
  abstract handlePasteText(text: string): void;
  abstract handleCtrlShiftC(e: JQueryKeyEventObject): void;
  abstract handleArrowPress(e: JQueryKeyEventObject): void;
  abstract showInputBubble(options: ShowInputBubbleOptions): void;
  abstract isValidCharacter(char: string): boolean;

  // Only necessary if subclasses don't override _calculateBubblePos
  _getBubbleOffsetClass(): string {
    throw new Error('Not implemented');
  }

  trackInput(action: string, moreMetadata?: object): void {
    if (moreMetadata == null) {
      moreMetadata = {};
    }
    // eslint-disable-next-line no-restricted-syntax
    analytics.track('editor', action, _.extend({source: this.inputSource}, moreMetadata));
  }

  registerListeners(): void {
    this.stopListening();
  }

  /**
   * If the user presses the meta key (CTRL or CMD), put their cursor in an input and set up handlers
   * for various keyboard shortcuts. We use a hidden #clipboard-input to listen to copy/cut/paste events,
   * since these require a selection.
   */
  anticipateMetaShortcut(): void {
    // The focus class is used so that the selection stays yellow when the user
    // presses CTRL and switches focus to the clipboard input. Once the keyboard shortcut is done, we
    // return focus to the tab and remove the focus class.
    const focusElement = this.focusElement();
    focusElement.addClass('focus');

    function focusAndRemove(): void {
      focusElement.removeClass('focus');
      _.defer(() => focusElement.focus());
    }

    const $input = $('#clipboard-input');
    $input
      .off()
      .on('copy', (e) => {
        this.handleCopy(e);
      })
      .on(
        'cut',
        _.once((e) => {
          this.handleCut(e);
        })
      ) // Don't allow holding CTRL-X
      .on(
        'paste',
        _.once((e) => {
          this.handlePaste(e);
        })
      ) // Don't allow holding CTRL-V
      .on('keydown', (e: JQueryKeyEventObject) => {
        if (keyboard.isCtrlA(e)) {
          this.handleCtrlA(e);
        }
        if (keyboard.isCtrlShiftC(e)) {
          this.handleCtrlShiftC(e);
        }
        if (keyboard.isCtrlI(e)) {
          this.handleCtrlI(e);
        }
        if (keyboard.isCtrlZ(e)) {
          this.handleCtrlZ(e);
        }
      })
      .on('keyup', focusAndRemove)
      .on('blur', () => {
        focusElement.removeClass('focus');
      });

    // Safari doesn't fire a copy event if there the selection is empty, so we add a space and select
    // that instead. Handle copy/cut work with the clipboard directly, so the value in the input should be
    // irrelevant.
    $input.val(' ').focus().select();
  }

  handleCtrlA(e: JQueryKeyEventObject): void {
    e.preventDefault();
    this.selectAll();
  }

  handleCtrlI(e: JQueryKeyEventObject): void {
    e.preventDefault();
    this.invertSelection();
  }

  handleCtrlZ(e: JQueryKeyEventObject): void {
    e.preventDefault();
    this.undo();
  }

  selectAll(): void {
    // Select the whole polymer
    const selection = this.getSelection();
    const length = selection.getPolymerLength();
    // Note that we need to both reset and set endpoints to clear any primers or other metadata
    // that are part of the selection.
    selection.reset(0);
    selection.setEndpoints(0, length);
    this.trackInput('select_all_bases', {length});
  }

  handleCopy(e: JQueryEventObject): void {
    const selection = this.getSelection();
    if (selection.isEmpty()) {
      return;
    }
    const clipboardHash = this.storeRegionInBenchlingClipboard(e);
    (e.originalEvent as ClipboardEvent).clipboardData!.setData('text/plain', clipboardHash);
    e.preventDefault();
    this.trackInput('copied_bases', {start: selection.start(), end: selection.end()});
  }

  handleCut(e: JQueryEventObject): void {
    // The selection is copied regardless of whether the user confirms or is allowed to cut the selection.
    const clipboardHash = this.storeRegionInBenchlingClipboard(e);
    (e.originalEvent as ClipboardEvent).clipboardData!.setData('text/plain', clipboardHash);
    e.preventDefault();
    this.handleDeleteSelection(INPUT_BUBBLE_OPERATION.CUT);
  }

  invertSelection(): void {
    // Invert the selection
    const selection = this.getSelection();
    if (!selection.canInvert()) {
      notifications.warn(`The current selection cannot be inverted, as it does not include the \
start or end of the polymer.`);
      return;
    }
    selection.invert();
    this.trackInput('invert_selection');
  }

  handlePaste(e: JQueryEventObject): void {
    const selection = this.getSelection();
    if (!selection.allowEdits()) {
      notifications.warn('Edits are not allowed for this file.', {id: 'PolymerMapTabView.editsNotAllowed'});
      return;
    }

    const text = (e.originalEvent as ClipboardEvent).clipboardData!.getData('text/plain');
    this.trackInput('paste_bases', {
      bases: text.slice(0, 100),
      before: {start: selection.start(), end: selection.end()},
      length: text.length,
    });
    e.preventDefault();
    this.showInputBubble({
      pasteText: text,
      doPaste: () => {
        this.handlePasteText(text);
        this.focusElement().focus();
      },
    });
  }

  handleKeydown(e: JQueryKeyEventObject): void {
    if (e.target !== e.currentTarget) {
      return;
    }

    if (keyboard.isControlKey(e)) {
      this.anticipateMetaShortcut();
      return;
    }

    if (keyboard.isAnArrowKey(e)) {
      this.handleArrowPress(e);
    }

    const selection = this.getSelection();
    const writable = selection.isWritable();
    const deletable = selection.isDeletable();

    if (keyboard.isADeleteKey(e)) {
      e.preventDefault();
      this.handleDeleteSelection(INPUT_BUBBLE_OPERATION.DELETE);
      return;
    }

    // Only letter keys should trigger the input bubble (e.g. Shift with e.key = 'Shift' should not insert
    //  the bases 'Shift').
    if (!keyboard.isLetterKey(e)) {
      return;
    }

    if (e.target === this.el && this.isValidCharacter(e.key)) {
      if (!deletable && !writable) {
        if (selection.allowEdits()) {
          interface ModelWithSequence {
            get(key: 'sequence'): BioJSSequence | AaSequence;
          }
          const model = this.model as ModelWithSequence;
          const polymer = model.get('sequence');
          if (
            !(polymer instanceof AaSequence) &&
            polymer.hasNullPhosphate() &&
            polymer.get('type') !== SequenceType.OLIGO
          ) {
            notifications.warn('Cannot insert after a null phosphate in long sequences.', {
              id: 'PolymerMapTabView.insertAfterNullPhosphate',
            });
          } else if (this.mapstate.polymer().getSequencePartsInSelection().length > 0) {
            notifications.warn('Cannot edit current selection while it spans a part.', {
              id: 'PolymerMapTabView.handleKeydown',
            });
          } else if (
            BenchlingConfig.MOLBIO_ENABLE_EDITING_BIOINTELLIGENT_LINKS &&
            this.mapstate.polymer().getTranscriptionsInSelection().length > 0
          ) {
            notifications.warn('Cannot edit current selection while it spans a transcription.', {
              id: 'PolymerMapTabView.handleKeydown',
            });
          } else {
            checkSoftInvariant(false, {
              message: 'Unexpected unwritable sequence',
              expirationDate: '2025-09-16',
              errorInDevAndTest: true,
            });
          }
        } else {
          notifications.warn('Edits are not allowed for this file.', {
            id: 'PolymerMapTabView.editsNotAllowed',
          });
        }
        return;
      }

      this.showInputBubble({});
    }
  }

  handleContextMenu(e: JQueryMouseEventObject): void {
    const polymer = this.model.get('sequence');
    if (polymer instanceof AaSequence) {
      ProteinContextMenus.openContextMenu(
        e,
        polymer,
        this as PolymerMapTabView<PolymerMapTabState<AaSequence>>
      );
      return;
    }
    const menuOptions = polymer.isOligo()
      ? selectioncontextmenu.OLIGO_DNA_MENU
      : selectioncontextmenu.DNA_MENU;
    // Note there are additional handlers on the map views (not map tab views) to handle the cursor on
    //  right click.
    selectioncontextmenu.open(e, polymer, menuOptions, this._contextMenuChoicesOptions());
  }

  _contextMenuChoicesOptions(): MenuChoicesOptions {
    // Only expected to be called for sequence-backed maps.
    const polymer = this.model.get('sequence') as Sequence;
    return {
      alignment: false,
      circular: polymer.circular(),
      polymerMapTabView: this as PolymerMapTabView<PolymerMapTabState<Sequence>>,
      hasTrim: polymer.get('trim') != null,
    };
  }

  handleDeleteSelection(operation: INPUT_BUBBLE_OPERATION.CUT | INPUT_BUBBLE_OPERATION.DELETE): void {
    const selection = this.getSelection();
    if (selection.isEmpty()) {
      return;
    }
    if (!selection.isDeletable()) {
      if (selection.allowEdits()) {
        if (this.mapstate.polymer().getSequencePartsInSelection().length > 0) {
          notifications.warn('Cannot delete current selection while it spans a part.', {
            id: 'PolymerMapTabView.handleKeydown',
          });
        } else if (
          BenchlingConfig.MOLBIO_ENABLE_EDITING_BIOINTELLIGENT_LINKS &&
          this.mapstate.polymer().getTranscriptionsInSelection().length > 0
        ) {
          notifications.warn('Cannot edit current selection while it spans a transcription.', {
            id: 'PolymerMapTabView.handleKeydown',
          });
        } else {
          checkSoftInvariant(false, {
            message: 'Unexpected unwritable sequence',
            expirationDate: '2025-09-16',
            errorInDevAndTest: true,
          });
        }
      } else {
        notifications.warn('Edits are not allowed for this file.', {
          id: 'PolymerMapTabView.editsNotAllowed',
        });
      }
      return;
    }
    const analyticsAction = operation === INPUT_BUBBLE_OPERATION.CUT ? 'cut_bases' : 'deleted_bases';
    this.trackInput(analyticsAction, {start: selection.start(), end: selection.end()});
    this.showInputBubble({operation});
  }

  _getCursorElement(): JQuery {
    const selection = this.getSelection();
    if (selection.length() > 0) {
      return this.$el.find('.selection-line').eq(0);
    } else {
      return this.$el.find('.map-cursor-line:not(.hide)');
    }
  }

  // Returns null if the bubble cannot be rendered anymore (e.g., user scrolled it off screen)
  _calculateBubblePos(): null | InputBubblePositionDetails {
    const alignElement = this._getCursorElement();
    if (alignElement.length === 0) {
      return null;
    }
    const cursorOffset = alignElement.offset()!;
    if (cursorOffset.left === 0) {
      return null;
    }
    const parentOffset = this.$el.offset()!;
    let top = cursorOffset.top - parentOffset.top + this.$el.scrollTop();
    let left = cursorOffset.left - parentOffset.left;
    const width = this.$el.innerWidth() - left;
    let offset = 0;
    const endPadding = 10;
    if (width < BUBBLE_WIDTH - endPadding) {
      left = this.$el.innerWidth() - BUBBLE_WIDTH + endPadding;
      offset = BUBBLE_WIDTH - width - endPadding;
    }
    const startPadding = 14;
    if (left < startPadding) {
      const diff = startPadding - left;
      left += diff;
      offset = -diff;
    }

    top += (alignElement.attr('y2') as unknown as number) - (alignElement.attr('y1') as unknown as number);

    // If user is inserting amino acids near bottom of page and bubble is going off the screen,
    // position the bubble above the slice
    const isPositionedAbove = this.allowBubblePositionedAboveSlice && top + 100 > this.$el.height();
    if (isPositionedAbove) {
      ({top} = alignElement.parent().find(this._getBubbleOffsetClass()).offset()!);
      top += this.$el.scrollTop() - 105;
      if (top > this.$el.height()) {
        return null;
      }
    }

    return {top, left, offset, isPositionedAbove};
  }

  repositionBubble(): void {
    if (this.inputBubbleView != null) {
      const pos = this._calculateBubblePos();
      if (pos == null || pos.top < 0) {
        this.inputBubbleView.remove();
      } else {
        this.inputBubbleView.updatePosition(pos);
        this.inputBubbleView.render();
      }
    }
  }

  getMoveEnd(): number {
    const selection = this.getSelection();
    return (selection.get(selection.freeEnd()) ?? selection.get(selection.anchorEnd()))!;
  }
}

PolymerMapTabView.initClass();

export default PolymerMapTabView;
